import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Subscription } from 'rxjs';
import { useObservable } from '@/shared/hooks/useObservable';
import { authService } from '@/features/auth/services/auth.service';
import { notificationService } from '../services/notification.service';
import { flightCategoriesService } from '../services/flightCategories.service';
// import { addUasDevice, getUasTypes } from '@/features/devices/api/devices.api'; // used by disabled AddDeviceForm
import { addOperator } from '@/features/operators/api/operators.api';
import { formatDrfError } from '@/shared/api/http';
// import type { UASTypeOption } from '@/features/devices/api/devices.api'; // used by disabled AddDeviceForm
import { Input } from '@/shared/components/Input';
import { Select } from '@/shared/components/Select';
import { Button } from '@/shared/components/Button';

function FieldLabel({ sl, en }: { sl: string; en: string }) {
  return (
    <label className="text-xs">
      <span className="text-slate-300 font-medium">{sl}</span>
      <span className="text-slate-500"> / {en}</span>
    </label>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <div className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">{children}</div>;
}

// Disabled for now (see StepOperator's commented-out usage below). Kept for re-enabling later.
// function AddDeviceForm({ onDone }: { onDone: () => void }) {
//   const [uasTypes, setUasTypes] = useState<UASTypeOption[]>([]);
//   const [selectedTypeId, setSelectedTypeId] = useState<number | 'new'>('new');
//   const [typeName, setTypeName] = useState('');
//   const [manufacturer, setManufacturer] = useState('');
//   const [serialNumber, setSerialNumber] = useState('');
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);
//   const subRef = useRef<Subscription | null>(null);
//
//   useEffect(() => {
//     const sub = getUasTypes().subscribe({ next: types => setUasTypes(types) });
//     return () => sub.unsubscribe();
//   }, []);
//
//   function handleSubmit(e: React.FormEvent) {
//     e.preventDefault();
//     setError(null);
//     setLoading(true);
//     subRef.current?.unsubscribe();
//     subRef.current = addUasDevice({
//       serialNumber,
//       existingTypeId: selectedTypeId !== 'new' ? selectedTypeId : undefined,
//       newTypeName: selectedTypeId === 'new' ? typeName : undefined,
//       newTypeManufacturer: selectedTypeId === 'new' ? manufacturer : undefined,
//     }).subscribe({
//       next: () => { setLoading(false); onDone(); },
//       error: (err) => { setError(formatDrfError(err, 'Napaka / Error saving device')); setLoading(false); },
//     });
//   }
//
//   return (
//     <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-700 rounded-lg p-3 space-y-3 mt-1">
//       <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Nova naprava / New device</div>
//
//       <div className="space-y-1">
//         <FieldLabel sl="Tip naprave" en="UAS type" />
//         <Select
//           value={String(selectedTypeId)}
//           onChange={e => setSelectedTypeId(e.target.value === 'new' ? 'new' : Number(e.target.value))}
//         >
//           <option value="new">+ Nov tip / New type</option>
//           {uasTypes.map(t => (
//             <option key={t.id} value={t.id}>{t.name} ({t.manufacturer})</option>
//           ))}
//         </Select>
//       </div>
//
//       {selectedTypeId === 'new' && (
//         <>
//           <div className="space-y-1">
//             <FieldLabel sl="Naziv tipa" en="Type name" />
//             <Input required value={typeName} onChange={e => setTypeName(e.target.value)} placeholder="DJI Mini 4 Pro" />
//           </div>
//           <div className="space-y-1">
//             <FieldLabel sl="Proizvajalec" en="Manufacturer" />
//             <Input required value={manufacturer} onChange={e => setManufacturer(e.target.value)} placeholder="DJI" />
//           </div>
//         </>
//       )}
//
//       <div className="space-y-1">
//         <FieldLabel sl="Serijska številka" en="Serial number" />
//         <Input required value={serialNumber} onChange={e => setSerialNumber(e.target.value)} placeholder="SN123456" />
//       </div>
//
//       {error && <p className="text-rose-400 text-xs whitespace-pre-wrap">{error}</p>}
//
//       <div className="flex gap-2">
//         <Button type="submit" disabled={loading} className="flex-1 justify-center text-sm py-1.5">
//           {loading ? 'Shranjevanje...' : 'Dodaj / Add'}
//         </Button>
//         <button
//           type="button"
//           onClick={onDone}
//           className="px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200 border border-slate-700 rounded-lg"
//         >
//           Prekliči / Cancel
//         </button>
//       </div>
//     </form>
//   );
// }

function AddOperatorForm({ onDone }: { onDone: (registrationNumber: string) => void }) {
  const [name, setName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [registrationValidUntil, setRegistrationValidUntil] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const subRef = useRef<Subscription | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    subRef.current?.unsubscribe();
    subRef.current = addOperator({ name, registrationNumber, registrationValidUntil }).subscribe({
      next: (regNo) => { setLoading(false); onDone(regNo); },
      error: (err) => { console.log(err);setError(formatDrfError(err, 'Napaka / Error saving operator')); setLoading(false); },
    });
  }

  return (
    <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-700 rounded-lg p-3 space-y-3 mt-1">
      <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Nov operator / New operator</div>

      <div className="space-y-1">
        <FieldLabel sl="Naziv operatorja" en="Operator name" />
        <Input required value={name} onChange={e => setName(e.target.value)} placeholder="Drones d.o.o." />
      </div>

      <div className="space-y-1">
        <FieldLabel sl="Reg. številka" en="Registration number" />
        <Input required value={registrationNumber} onChange={e => setRegistrationNumber(e.target.value)} placeholder="SVN-001" />
      </div>

      <div className="space-y-1">
        <FieldLabel sl="Veljavnost do" en="Valid until" />
        <Input required type="date" value={registrationValidUntil} onChange={e => setRegistrationValidUntil(e.target.value)} />
      </div>

      {error && <p className="text-rose-400 text-xs whitespace-pre-wrap">{error}</p>}

      <div className="flex gap-2">
        <Button type="submit" disabled={loading} className="flex-1 justify-center text-sm py-1.5">
          {loading ? 'Shranjevanje...' : 'Dodaj / Add'}
        </Button>
        <button
          type="button"
          onClick={() => onDone('')}
          className="px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200 border border-slate-700 rounded-lg"
        >
          Prekliči / Cancel
        </button>
      </div>
    </form>
  );
}

export function StepOperator() {
  const user = useObservable(authService.user$, null);
  const data = useObservable(notificationService.formData$, notificationService.formData$.value);
  const categories = useObservable(flightCategoriesService.categories$, flightCategoriesService.categories$.value);
  const categoriesError = useObservable(flightCategoriesService.error$, flightCategoriesService.error$.value);
  // const [showAddDevice, setShowAddDevice] = useState(false); // disabled, see AddDeviceForm above
  const [showAddOperator, setShowAddOperator] = useState(false);

  useEffect(() => {
    flightCategoriesService.load();
  }, []);

  useEffect(() => {
    if (!user) return;
    const current = notificationService.formData$.value;
    const updates: Partial<typeof current> = {};
    if (!current.pilotPhone && user.phone) updates.pilotPhone = user.phone;
    if (!current.operatorId && user.operators.length > 0) {
      const op = user.operators[0];
      updates.operatorId = op.id;
      updates.operatorRegistrationNumber = op.registrationNumber;
    }
    if (Object.keys(updates).length > 0) notificationService.updateFormData(updates);
  }, [user]);

  if (!user) return null;

  const selectedUas = user.uasList.find(u => u.id === data.uasId);

  function handleOperatorDone(registrationNumber: string) {
    setShowAddOperator(false);
    if (!registrationNumber) return;
    const updatedUser = authService.user$.value;
    if (!updatedUser) return;
    const op = updatedUser.operators.find(o => o.registrationNumber === registrationNumber);
    if (op) {
      notificationService.updateFormData({
        operatorId: op.id,
        operatorRegistrationNumber: op.registrationNumber,
      });
    }
  }

  return (
    <div className="space-y-4">
      <SectionTitle>Operator / UAS</SectionTitle>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <FieldLabel sl="Naziv operatorja" en="UAS Operator" />
          <button
            type="button"
            onClick={() => setShowAddOperator(v => !v)}
            className="text-xs text-blue-400 hover:text-blue-300"
          >
            {showAddOperator ? 'Zapri / Close' : '+ Dodaj operatorja / Add operator'}
          </button>
        </div>
        {user.operators.length > 0 ? (
          <Select
            value={data.operatorId}
            onChange={e => {
              const op = user.operators.find(o => o.id === e.target.value);
              notificationService.updateFormData({
                operatorId: e.target.value,
                operatorRegistrationNumber: op?.registrationNumber ?? '',
              });
            }}
          >
            <option value="">-- Izberi / Select --</option>
            {user.operators.map(op => (
              <option key={op.id} value={op.id}>{op.name} - {op.registrationNumber}</option>
            ))}
          </Select>
        ) : (
          <p className="text-xs text-amber-400">
            Ni operatorja / No operator linked.
          </p>
        )}
        {showAddOperator && (
          <AddOperatorForm onDone={handleOperatorDone} />
        )}
      </div>

      <div className="space-y-1">
        <FieldLabel sl="Telefonska številka pilota" en="Pilot tel." />
        <Input
          prefilled
          value={data.pilotPhone || user.phone}
          onChange={e => notificationService.updateFormData({ pilotPhone: e.target.value })}
        />
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <FieldLabel sl="Tip naprave" en="UAS type" />
          {/* Add-device button disabled, see AddDeviceForm above */}
        </div>
        <Select
          value={data.uasId}
          onChange={e => {
            const uas = user.uasList.find(u => u.id === e.target.value);
            notificationService.updateFormData({
              uasId: e.target.value,
              ...(uas?.vlosMaxDistanceM != null ? { maxDistance: uas.vlosMaxDistanceM } : {}),
            });
          }}
        >
          <option value="">-- Izberi / Select --</option>
          {user.uasList.map(uas => (
            <option key={uas.id} value={uas.id}>
              {uas.type}
              {uas.mtomKg != null ? ` (MTOM ${uas.mtomKg} kg)` : ''}
              {uas.vlosMaxDistanceM != null ? ` · VLOS ${uas.vlosMaxDistanceM} m` : ''}
            </option>
          ))}
        </Select>
        {/*{showAddDevice && (*/}
        {/*  <AddDeviceForm onDone={() => setShowAddDevice(false)} />*/}
        {/*)}*/}
      </div>

      <div className="space-y-1">
        <FieldLabel sl="Serijska številka" en="Serial no." />
        <Input prefilled value={selectedUas?.serialNumber ?? ''} readOnly />
      </div>

      <div className="space-y-1">
        <FieldLabel sl="Reg. številka operatorja" en="Operator reg. no." />
        <Input
          prefilled
          value={data.operatorRegistrationNumber}
          onChange={e => notificationService.updateFormData({ operatorRegistrationNumber: e.target.value })}
        />
      </div>

      <div className="space-y-1">
        <FieldLabel sl="Kategorija" en="Category" />
        <Select
          value={data.category}
          onChange={e => notificationService.updateFormData({ category: e.target.value })}
        >
          <option value="">-- Izberi / Select --</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name} / {cat.nameEn}</option>
          ))}
        </Select>
        {categoriesError && (
          <p className="text-xs text-red-400">
            Napaka pri nalaganju kategorij. / Error loading categories.{' '}
            <button
              type="button"
              onClick={() => flightCategoriesService.load()}
              className="underline hover:text-red-300"
            >
              Poskusi znova / Retry
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
